import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';


import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Search,
  RefreshCw,
  X,
  Plus,
  MoreVertical,
  Eye,
  Edit,
  Check,
  Receipt,
  XCircle,
  Calendar,
  BarChart3,
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import settlementService from '../services/settlementService';
import Layout from '../components/Layout';
import useEnhancedPagination from '../hooks/useEnhancedPagination';
import useFilterPersistence from '../hooks/useFilterPersistence';
import authService from '../services/authService';

const SettlementsPage = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const isAdmin = user?.role === 'admin';
  const canViewPrices = user && ['admin', 'accounting'].includes(user.role);

  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Calculate default date range (last 30 days)
  const getDefaultDateRange = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };

  // Use filter persistence hook with default values
  const [persistedState, setPersistedState] = useFilterPersistence('settlements', {
    filters: {
      settlement_id: '',
      supplier_name: '',
      status: '',
      approval_status: '',
      payment_status: ''
    },
    dateRange: getDefaultDateRange(),
    page: 1,
    page_size: 10,
    sortBy: 'created_at',
    sortOrder: 'desc'
  });

  const filters = persistedState.filters;
  const dateRange = persistedState.dateRange;

  const setFilters = (newFilters) => {
    setPersistedState(prev => ({
      ...prev,
      filters: typeof newFilters === 'function' ? newFilters(prev.filters) : newFilters
    }));
  };

  const setDateRange = (newDateRange) => {
    setPersistedState(prev => ({
      ...prev,
      dateRange: typeof newDateRange === 'function' ? newDateRange(prev.dateRange) : newDateRange
    }));
  };

  // Enhanced pagination hook with persisted initial values
  const {
    pagination,
    sorting,
    handlePageChange: originalHandlePageChange,
    handleSortChange: originalHandleSortChange,
    updatePaginationData,
    resetPagination,
    paginationInfo
  } = useEnhancedPagination({
    initialPage: persistedState.page,
    initialPageSize: persistedState.page_size,
    defaultSortBy: persistedState.sortBy,
    defaultSortOrder: persistedState.sortOrder,
    enableVirtualization: true,
    virtualizationThreshold: 50
  });

  // Wrap pagination handlers to persist changes
  const handlePageChange = useCallback((newPage) => {
    originalHandlePageChange(null, newPage);
    setPersistedState(prev => ({ ...prev, page: newPage }));
  }, [originalHandlePageChange, setPersistedState]);

  const handleSortChange = useCallback((field) => {
    const isAsc = sorting.orderBy === field && sorting.order === 'asc';
    const newSorting = { orderBy: field, order: isAsc ? 'desc' : 'asc' };
    originalHandleSortChange(newSorting);
    setPersistedState(prev => ({
      ...prev,
      sortBy: newSorting.orderBy,
      sortOrder: newSorting.order
    }));
  }, [sorting, originalHandleSortChange, setPersistedState]);

  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: '', settlement: null, reason: '' });
  const fetchingRef = useRef(false);

  const loadSettlements = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    try {
      setLoading(true);
      setError('');

      const response = await settlementService.getSettlements(
        pagination.page,
        pagination.page_size,
        {
          ...filters,
          start_date: dateRange.startDate,
          end_date: dateRange.endDate,
          sort_by: sorting.orderBy,
          sort_order: sorting.order
        }
      );

      if (response.settlements && Array.isArray(response.settlements)) {
        setSettlements(response.settlements);
      } else if (Array.isArray(response)) {
        setSettlements(response);
      } else {
        console.error('Unexpected response structure:', response);
        setSettlements([]);
      }

      if (response.pagination) {
        updatePaginationData(response);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load settlements');
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [pagination.page, pagination.page_size, filters, sorting, dateRange, updatePaginationData]);

  useEffect(() => {
    loadSettlements();
  }, [loadSettlements]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value === 'all' ? '' : value
    }));
  };

  const handleFilterSubmit = (e) => {
    if (e) e.preventDefault();
    resetPagination();
  };

  const handleClearFilter = (filterName) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: ''
    }));
  };

  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleClearAllFilters = () => {
    setFilters({
      settlement_id: '',
      supplier_name: '',
      status: '',
      approval_status: '',
      payment_status: ''
    });
    setDateRange(getDefaultDateRange());
  };

  const handleAction = (action, settlement) => {
    if (action === 'view') {
      navigate(`/settlements/${settlement._id}/view`, { state: { settlementData: settlement } });
    } else if (action === 'edit') {
      navigate(`/settlements/${settlement._id}/edit`);
    } else if (['submit', 'approve', 'reject', 'cancel', 'markPaid'].includes(action)) {
      setConfirmDialog({ open: true, action, settlement, reason: '' });
    }
  };

  const handleConfirmAction = async () => {
    const { action, settlement, reason } = confirmDialog;
    setConfirmDialog({ open: false, action: '', settlement: null, reason: '' });

    try {
      if (action === 'submit') {
        await settlementService.submitForApproval(settlement._id);
        setSettlements(prev => prev.map(s =>
          s._id === settlement._id ? { ...s, approval_status: 'pending', status: 'pending_approval' } : s
        ));
        setError('');
        setTimeout(() => alert('结算单已提交审批！'), 100);
      } else if (action === 'approve') {
        await settlementService.approveSettlement(settlement._id);
        setSettlements(prev => prev.map(s =>
          s._id === settlement._id ? { ...s, approval_status: 'approved', status: 'approved' } : s
        ));
        setError('');
        setTimeout(() => alert('结算单已批准！'), 100);
      } else if (action === 'reject') {
        await settlementService.rejectSettlement(settlement._id, reason);
        setSettlements(prev => prev.map(s =>
          s._id === settlement._id ? { ...s, approval_status: 'rejected' } : s
        ));
        setError('');
        setTimeout(() => alert('结算单已拒绝！'), 100);
      } else if (action === 'cancel') {
        await settlementService.deleteSettlement(settlement._id);
        setError('');
        setTimeout(() => alert('结算单已取消！'), 100);
      } else if (action === 'markPaid') {
        await settlementService.markAsPaid(settlement._id);
        setSettlements(prev => prev.map(s =>
          s._id === settlement._id ? { ...s, payment_status: 'paid', status: 'paid' } : s
        ));
        setError('');
        setTimeout(() => alert('结算单已标记为已付款！'), 100);
      }

      loadSettlements();
    } catch (err) {
      setError(err.response?.data?.error || `操作失败`);
    }
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'draft': return 'bg-muted text-muted-foreground';
      case 'pending_approval': return 'bg-info/10 text-info border-info/30';
      case 'approved': return 'bg-success/10 text-success border-success/30';
      case 'paid': return 'bg-primary/10 text-primary border-primary/30';
      case 'refunded': return 'bg-warning/10 text-warning border-warning/30';
      case 'cancelled': return 'bg-destructive/10 text-destructive border-destructive/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getApprovalStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'bg-warning/20 text-warning border-warning/30';
      case 'approved': return 'bg-success/20 text-success border-success/30';
      case 'rejected': return 'bg-destructive/20 text-destructive border-destructive/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getPaymentStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'unpaid': return 'bg-muted text-muted-foreground';
      case 'partially_paid': return 'bg-info/10 text-info border-info/30';
      case 'paid': return 'bg-success/10 text-success border-success/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const getStatusLabel = (status, type = 'status') => {
    if (type === 'approval') {
      switch (status?.toLowerCase()) {
        case 'pending': return '待审批';
        case 'approved': return '已批准';
        case 'rejected': return '已拒绝';
        default: return status || '待审批';
      }
    } else if (type === 'payment') {
      switch (status?.toLowerCase()) {
        case 'unpaid': return '未付款';
        case 'partially_paid': return '部分付款';
        case 'paid': return '已付款';
        default: return status || '未付款';
      }
    } else {
      switch (status?.toLowerCase()) {
        case 'draft': return '草稿';
        case 'pending_approval': return '待审批';
        case 'approved': return '已批准';
        case 'paid': return '已付款';
        case 'refunded': return '已退款';
        case 'cancelled': return '已取消';
        default: return status || '草稿';
      }
    }
  };

  const canShowAction = (action, settlement) => {
    const status = settlement?.status?.toLowerCase();
    const approvalStatus = settlement?.approval_status?.toLowerCase();

    switch (action) {
      case 'submit':
        return status === 'draft';
      case 'approve':
        return isAdmin && approvalStatus === 'pending';
      case 'reject':
        return isAdmin && approvalStatus === 'pending';
      case 'cancel':
        return (status === 'draft' || approvalStatus === 'rejected') && isAdmin;
      case 'markPaid':
        return approvalStatus === 'approved' && settlement?.payment_status !== 'paid';
      default:
        return false;
    }
  };

  const headCells = useMemo(() => {
    const cells = [
      { id: 'settlement_id', label: '结算单编号' },
      { id: 'supplier_name', label: '供应商' },
      { id: 'status', label: '状态' },
      { id: 'approval_status', label: '审批状态' },
      { id: 'payment_status', label: '付款状态' },
      { id: 'total_amount', label: '总金额', sortable: false },
      { id: 'purchase_order_count', label: '采购单数量', sortable: false },
      { id: 'created_at', label: '创建日期' },
      { id: 'actions', label: '操作', sortable: false }
    ];
    return cells;
  }, []);

  const SortIcon = ({ columnId }) => {
    if (sorting.orderBy !== columnId) return <ArrowUpDown className="ml-1 h-4 w-4 text-muted-foreground" />;
    return sorting.order === 'asc'
      ? <ArrowUp className="ml-1 h-4 w-4" />
      : <ArrowDown className="ml-1 h-4 w-4" />;
  };

  const getDialogTitle = () => {
    switch (confirmDialog.action) {
      case 'submit': return '提交审批';
      case 'approve': return '批准结算单';
      case 'reject': return '拒绝结算单';
      case 'cancel': return '取消结算单';
      case 'markPaid': return '标记为已付款';
      default: return '';
    }
  };

  const getDialogDescription = () => {
    switch (confirmDialog.action) {
      case 'submit':
        return `确定要提交结算单 ${confirmDialog.settlement?.settlement_id} 进行审批吗？`;
      case 'approve':
        return `确定要批准结算单 ${confirmDialog.settlement?.settlement_id} 吗？`;
      case 'reject':
        return `确定要拒绝结算单 ${confirmDialog.settlement?.settlement_id} 吗？`;
      case 'cancel':
        return `确定要取消结算单 ${confirmDialog.settlement?.settlement_id} 吗？此操作不可撤销。`;
      case 'markPaid':
        return `确定要将结算单 ${confirmDialog.settlement?.settlement_id} 标记为已付款吗？`;
      default:
        return '';
    }
  };

  return (
    <Layout title="结算管理" activeTab="settlements">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="flex items-center justify-between">
              {error}
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setError('')}>
                <X className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Header with Action Buttons */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-foreground">结算单</h1>
          <div className="flex gap-2">
            {canViewPrices && (
              <Button variant="outline">
                <BarChart3 className="mr-2 h-4 w-4" />
                成本分析
              </Button>
            )}
            <Button onClick={() => navigate('/settlements/new')}>
              <Plus className="mr-2 h-4 w-4" />
              新建结算单
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <form onSubmit={handleFilterSubmit} className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[180px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    name="settlement_id"
                    placeholder="按结算单编号筛选..."
                    value={filters.settlement_id}
                    onChange={handleFilterChange}
                    className="pl-10 pr-8"
                  />
                  {filters.settlement_id && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                      onClick={() => handleClearFilter('settlement_id')}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex-1 min-w-[180px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    name="supplier_name"
                    placeholder="按供应商名称筛选..."
                    value={filters.supplier_name}
                    onChange={handleFilterChange}
                    className="pl-10 pr-8"
                  />
                  {filters.supplier_name && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                      onClick={() => handleClearFilter('supplier_name')}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Status Filters */}
              <div className="min-w-[120px]">
                <Label className="text-xs mb-1 block">状态</Label>
                <Select value={filters.status || 'all'} onValueChange={(v) => handleSelectFilterChange('status', v)}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全部</SelectItem>
                    <SelectItem value="draft">草稿</SelectItem>
                    <SelectItem value="pending_approval">待审批</SelectItem>
                    <SelectItem value="approved">已批准</SelectItem>
                    <SelectItem value="paid">已付款</SelectItem>
                    <SelectItem value="cancelled">已取消</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="min-w-[120px]">
                <Label className="text-xs mb-1 block">审批状态</Label>
                <Select value={filters.approval_status || 'all'} onValueChange={(v) => handleSelectFilterChange('approval_status', v)}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全部</SelectItem>
                    <SelectItem value="pending">待审批</SelectItem>
                    <SelectItem value="approved">已批准</SelectItem>
                    <SelectItem value="rejected">已拒绝</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="min-w-[120px]">
                <Label className="text-xs mb-1 block">付款状态</Label>
                <Select value={filters.payment_status || 'all'} onValueChange={(v) => handleSelectFilterChange('payment_status', v)}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全部</SelectItem>
                    <SelectItem value="unpaid">未付款</SelectItem>
                    <SelectItem value="partially_paid">部分付款</SelectItem>
                    <SelectItem value="paid">已付款</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="h-8 w-px bg-border mx-1" />

              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div className="space-y-1">
                  <Label className="text-xs">开始日期</Label>
                  <Input
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                    className="h-9 w-[140px]"
                  />
                </div>
                <span className="text-sm text-muted-foreground">至</span>
                <div className="space-y-1">
                  <Label className="text-xs">结束日期</Label>
                  <Input
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                    className="h-9 w-[140px]"
                  />
                </div>
              </div>

              <Button
                type="submit"
                variant="ghost"
                size="icon"
                disabled={loading}
                title="刷新"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleClearAllFilters}
                disabled={loading}
                title="清除所有筛选"
              >
                <X className="h-4 w-4" />
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Settlements Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50 border-b">
                        {headCells.map((headCell) => (
                          <th
                            key={headCell.id}
                            className="px-4 py-3 text-left font-semibold text-foreground"
                          >
                            {headCell.id !== 'actions' && headCell.sortable !== false ? (
                              <button
                                className="flex items-center hover:text-primary transition-colors"
                                onClick={() => handleSortChange(headCell.id)}
                              >
                                {headCell.label}
                                <SortIcon columnId={headCell.id} />
                              </button>
                            ) : (
                              headCell.label
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {settlements.map((settlement) => (
                        <tr key={settlement._id} className="hover:bg-muted/30 cursor-pointer">
                          <td className="px-4 py-3" onClick={() => navigate(`/settlements/${settlement._id}/view`)}>
                            <span className="text-primary underline hover:text-primary/80 transition-colors font-medium">
                              {settlement.settlement_id}
                            </span>
                          </td>
                          <td className="px-4 py-3" onClick={() => navigate(`/settlements/${settlement._id}/view`)}>
                            {settlement.supplier_name}
                          </td>
                          <td className="px-4 py-3" onClick={() => navigate(`/settlements/${settlement._id}/view`)}>
                            <Badge variant="outline" className={getStatusClass(settlement.status)}>
                              {getStatusLabel(settlement.status)}
                            </Badge>
                          </td>
                          <td className="px-4 py-3" onClick={() => navigate(`/settlements/${settlement._id}/view`)}>
                            <Badge variant="outline" className={getApprovalStatusClass(settlement.approval_status)}>
                              {getStatusLabel(settlement.approval_status, 'approval')}
                            </Badge>
                          </td>
                          <td className="px-4 py-3" onClick={() => navigate(`/settlements/${settlement._id}/view`)}>
                            <Badge variant="outline" className={getPaymentStatusClass(settlement.payment_status)}>
                              {getStatusLabel(settlement.payment_status, 'payment')}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 font-bold" onClick={() => navigate(`/settlements/${settlement._id}/view`)}>
                            {formatCurrency(settlement.total_amount || 0)}
                          </td>
                          <td className="px-4 py-3" onClick={() => navigate(`/settlements/${settlement._id}/view`)}>
                            {settlement.purchase_orders?.length || 0} 项
                          </td>
                          <td className="px-4 py-3" onClick={() => navigate(`/settlements/${settlement._id}/view`)}>
                            {formatDate(settlement.created_at)}
                          </td>
                          <td className="px-4 py-3">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleAction('view', settlement)}>
                                  <Eye className="mr-2 h-4 w-4" />
                                  查看详情
                                </DropdownMenuItem>
                                {canShowAction('edit', settlement) && (
                                  <DropdownMenuItem onClick={() => handleAction('edit', settlement)}>
                                    <Edit className="mr-2 h-4 w-4" />
                                    编辑结算单
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                {canShowAction('submit', settlement) && (
                                  <DropdownMenuItem onClick={() => handleAction('submit', settlement)}>
                                    <Receipt className="mr-2 h-4 w-4" />
                                    提交审批
                                  </DropdownMenuItem>
                                )}
                                {canShowAction('approve', settlement) && (
                                  <DropdownMenuItem onClick={() => handleAction('approve', settlement)}>
                                    <Check className="mr-2 h-4 w-4" />
                                    批准
                                  </DropdownMenuItem>
                                )}
                                {canShowAction('reject', settlement) && (
                                  <DropdownMenuItem onClick={() => handleAction('reject', settlement)}>
                                    <X className="mr-2 h-4 w-4" />
                                    拒绝
                                  </DropdownMenuItem>
                                )}
                                {canShowAction('markPaid', settlement) && (
                                  <DropdownMenuItem onClick={() => handleAction('markPaid', settlement)}>
                                    标记付款
                                  </DropdownMenuItem>
                                )}
                                {canShowAction('cancel', settlement) && (
                                  <DropdownMenuItem
                                    onClick={() => handleAction('cancel', settlement)}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <XCircle className="mr-2 h-4 w-4" />
                                    取消结算单
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}

                      {settlements.length === 0 && (
                        <tr>
                          <td colSpan={headCells.length} className="px-4 py-8 text-center text-muted-foreground">
                            未找到结算单
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {pagination.total_pages > 1 && (
                  <div className="flex justify-center py-4 border-t">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page <= 1}
                      >
                        上一页
                      </Button>
                      <span className="text-sm text-muted-foreground px-4">
                        第 {pagination.page} / {pagination.total_pages} 页
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page >= pagination.total_pages}
                      >
                        下一页
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <p className="mt-4 text-sm text-muted-foreground">
          正在显示 {paginationInfo.startItem}-{paginationInfo.endItem} / {paginationInfo.totalItems} 条结算单
        </p>

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialog.open}
          onOpenChange={(open) => !open && setConfirmDialog({ open: false, action: '', settlement: null, reason: '' })}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{getDialogTitle()}</DialogTitle>
              <DialogDescription>{getDialogDescription()}</DialogDescription>
            </DialogHeader>
            {confirmDialog.action === 'reject' && (
              <div className="py-4">
                <Label htmlFor="reject-reason">拒绝原因</Label>
                <Textarea
                  id="reject-reason"
                  placeholder="请输入拒绝原因..."
                  value={confirmDialog.reason}
                  onChange={(e) => setConfirmDialog(prev => ({ ...prev, reason: e.target.value }))}
                  className="mt-2"
                  rows={3}
                />
              </div>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setConfirmDialog({ open: false, action: '', settlement: null, reason: '' })}
              >
                取消
              </Button>
              <Button
                variant={confirmDialog.action === 'cancel' || confirmDialog.action === 'reject' ? 'destructive' : 'default'}
                onClick={handleConfirmAction}
              >
                {confirmDialog.action === 'submit' && '提交'}
                {confirmDialog.action === 'approve' && '批准'}
                {confirmDialog.action === 'reject' && '拒绝'}
                {confirmDialog.action === 'cancel' && '取消'}
                {confirmDialog.action === 'markPaid' && '标记付款'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default SettlementsPage;

